import React, { useState, useEffect } from 'react';
import { useToast } from '../../Utils/toastContext';
import { useNavigate, useLocation } from 'react-router-dom';

const PricingPlanCards = ({ selectedPlan, onSelectPlan, planId }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [plansData, setPlansData] = useState([]);

  const formatCurrency = (amountCents, currency = 'AUD') => {
    if (amountCents == null || amountCents === undefined) {
      return 'Custom pricing';
    }

    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amountCents / 100);
  };

  const formatAgentRange = (minAgents, maxAgents) => {
    if (minAgents == null || maxAgents == null) {
      return 'Custom agent range';
    }

    return `${minAgents}–${maxAgents} agents`;
  };

  const getPlanFeatures = (version) => {
    const capabilities = version?.capabilities || {};
    const features = [];

    if (capabilities.risk_assessment) {
      features.push('Risk assessment');
    }

    if (capabilities.compliance_dashboard) {
      features.push('Compliance dashboard');
    }

    if (capabilities.training_audit_trail) {
      features.push('Training & audit trail');
    }

    if (capabilities.advanced_analytics) {
      features.push('Advanced analytics');
    }

    if (version?.included_kyc_scans != null) {
      features.push(`${version.included_kyc_scans} included KYC scans`);
    }

    if (version?.kyc_overage_allowed) {
      features.push('KYC overage allowed');
    }

    if (version?.support_tier) {
      features.push(`Support: ${version.support_tier.replace(/_/g, ' ')}`);
    }

    if (version?.onboarding_tier) {
      features.push(
        `Onboarding: ${version.onboarding_tier.replace(/_/g, ' ')}`,
      );
    }

    return features;
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getPlanList = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/subscription`);

      if (response?.status === 200 && Array.isArray(response?.data?.plans)) {
        setPlansData(response.data.plans);
      } else {
        if (response?.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response?.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getPlanList();
  }, []);

  const getAccentClasses = (index) => {
    const accents = [
      'from-[#375AE7] via-blue-500 to-cyan-400',
      'from-emerald-500 via-teal-500 to-cyan-500',
      'from-violet-600 via-indigo-500 to-blue-500',
      'from-amber-500 via-orange-500 to-rose-500',
    ];

    return accents[index % accents.length];
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {plansData.length === 0 ? (
        <div className="md:col-span-2 xl:col-span-3 rounded-3xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Loading plans...
        </div>
      ) : (
        plansData.map((plan, idx) => {
          const version = plan?.current_version || {};
          const features = getPlanFeatures(version);
          const extras = Array.isArray(version?.extras) ? version.extras : [];
          const activeSelection = selectedPlan?.id ?? selectedPlan;
          const isSelected =
            (activeSelection != null &&
              Number(activeSelection) === Number(plan.id)) ||
            (activeSelection == null &&
              planId != null &&
              Number(planId) === Number(plan.id));

          return (
            <div
              key={plan.id || idx}
              onClick={() => onSelectPlan(plan)}
              className={`group relative cursor-pointer overflow-hidden rounded-[28px] border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(55,90,231,0.35)] ${
                isSelected
                  ? 'border-[#375AE7] ring-2 ring-[#375AE7]/20 shadow-lg'
                  : 'border-slate-200 hover:border-[#375AE7]/30'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-50/80 via-white to-blue-50/70" />

              <div
                className={`absolute left-0 right-0 top-0 h-[5px] bg-gradient-to-r transition-all duration-300 ${getAccentClasses(idx)}`}
              />

              <div className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-600 shadow-sm">
                {version?.version || 'v1'}
              </div>

              {isSelected && (
                <div className="absolute left-4 top-4 rounded-full bg-[#375AE7] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white shadow-sm">
                  Selected
                </div>
              )}

              <div className="relative mt-6 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {plan?.name || 'Plan'}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">
                    {plan?.description || 'Flexible plan for your agency'}
                  </p>
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700">
                    {formatAgentRange(version?.min_agents, version?.max_agents)}
                  </span>
                  <span className="text-sm font-semibold text-slate-700">
                    {version?.included_kyc_scans ?? 0} scans
                  </span>
                </div>
              </div>

              <div className="relative mt-5 flex items-end gap-2">
                <p className="text-3xl font-extrabold text-[#375AE7]">
                  {version?.monthly_amount_cents != null
                    ? formatCurrency(
                        version.monthly_amount_cents,
                        version?.currency,
                      )
                    : 'Custom pricing'}
                </p>
                <span className="pb-1 text-sm text-slate-500">/ month</span>
              </div>

              <div className="relative mt-5 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                  Included features
                </p>
              </div>

              <ul className="relative mt-3 space-y-2 text-sm text-slate-700">
                {features.map((feature, i) => (
                  <li
                    key={`${plan.id}-${i}`}
                    className="flex items-start gap-2"
                  >
                    <span className="mt-0.5 text-emerald-500">✔</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {extras.length > 0 && (
                <div className="relative mt-5 rounded-2xl border border-slate-200 bg-gradient-to-r from-white to-blue-50/70 p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-sm">✨</span>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#375AE7]">
                      Extra benefits
                    </p>
                  </div>

                  <ul className="space-y-2 text-sm text-slate-700">
                    {extras.map((item, i) => (
                      <li
                        key={`${plan.id}-extra-${i}`}
                        className="flex items-start gap-2"
                      >
                        <span className="mt-0.5 text-[#375AE7]">＋</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default PricingPlanCards;
